'use client';

import { Input } from '@nextui-org/react';
import React, { useEffect, useMemo, useRef } from 'react';

const CODE_LENGTH = 6;

type Props = {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: boolean;
  /** 各桁に表示する placeholder（例: "123456" で各桁に "1" "2" ... を割り当て） */
  placeholder?: string;
};

/**
 * 6桁の数字 OTP コード入力コンポーネント。
 * - 各桁を個別の input に分割し、入力ごとに次のフィールドへフォーカス移動
 * - バックスペースで前のフィールドへ戻る
 * - ペーストで6桁を一括入力可能
 * - value は常に6桁に正規化（右側を空文字でパディング）
 */
const FormOTPInput: React.FC<Props> = ({
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  error = false,
  placeholder,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const digits = useMemo(() => {
    const normalized = value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    return Array.from({ length: CODE_LENGTH }, (_, i) => normalized[i] ?? '');
  }, [value]);

  useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  const emit = (next: string[]) => {
    const joined = next.join('').slice(0, CODE_LENGTH);
    onChange(joined);
    if (joined.length === CODE_LENGTH && onComplete) {
      onComplete(joined);
    }
  };

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    emit(next);

    if (digit && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      if (digits[index]) {
        const next = [...digits];
        next[index] = '';
        emit(next);
        return;
      }
      if (index > 0) {
        const prev = index - 1;
        const next = [...digits];
        next[prev] = '';
        emit(next);
        inputRefs.current[prev]?.focus();
      }
      return;
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }
    if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!text) return;
    event.preventDefault();
    const next = Array.from({ length: CODE_LENGTH }, (_, i) => text[i] ?? '');
    emit(next);
    const focusIndex = Math.min(text.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="flex w-full justify-between gap-2" aria-label="OTP code input">
      {digits.map((digit, index) => (
        <Input
          key={index}
          ref={el => {
            inputRefs.current[index] = el;
          }}
          value={digit}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          isDisabled={disabled}
          inputMode="numeric"
          maxLength={1}
          placeholder={placeholder ? placeholder[index] ?? '' : undefined}
          radius="sm"
          size="lg"
          classNames={{
            input: 'text-center text-3xl font-semibold placeholder:text-default-200 placeholder:font-normal',
            // hover/focus 時の bg を白固定にして暗くならないようにする
            inputWrapper: error
              ? 'h-16 border border-danger data-[hover=true]:border-danger'
              : 'h-16 bg-white border border-default-300 shadow-md data-[hover=true]:bg-white data-[hover=true]:border-default-400 group-data-[focus=true]:bg-white group-data-[focus=true]:border-primary',
          }}
          aria-label={`OTP digit ${index + 1}`}
        />
      ))}
    </div>
  );
};

export default FormOTPInput;
